import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { AppActionButton } from "../../../features/ImageSuite/Components/Shared/AppActionButton";
import {
  convertUtcToLocalDateTime,
  formatDateAndHour,
  hasFlag,
} from "../../../store/one-query/useMethodHelper";
import {
  ImageAppHistoryItem,
  ImageSuiteApps,
  ModelImageFXModelName,
  ModelImageFXName,
  SuiteApps,
} from "../../../Helper";
import { SuggestionApp } from "../../../features/ImageSuite/ImageSuiteHistory/Layout/ImageSuiteHistoryContainer";
import { returnAppIcon } from "../View/SuiteHistoryView";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

import {
  OptionBtnIconStyle,
  tooltipStyles,
} from "../../../features/TextSuite/Components/TextSuitesBody";
import { ReviewSuggestionsApps } from "./ReviewSuggestionsApps";
import { useAppSelector } from "../../../store/hooks";
import { InputAndOutputTypes } from "../../../Types/ImageSuite-types";
import { useImageSuiteGetImageSettings } from "../../../store/Apis/SuitesApps/ImageSuitesApi";

interface HistoryItemReviewProps {
  itemData: ImageAppHistoryItem | undefined;
  handleRegenerateItem: (itemType: string) => void;
  isLoading: boolean;
  suggestionsImageApps: SuggestionApp[];
  btnActionText?: string;
}

export const HistoryItemReview = ({
  itemData,
  handleRegenerateItem,
  isLoading,
  suggestionsImageApps,
  btnActionText
}: HistoryItemReviewProps) => {
  const [isTitleCoped, setIsTitleCoped] = useState(false);
  const [titleCoped, setTitleCoped] = useState("");
  const appSuiteCosts = useAppSelector((s) => s.User.AppSuiteCosts);
    const {
    refetch: GetImageSettings,
    data: ImageSettingsData,
  } = useImageSuiteGetImageSettings();
    useEffect(() => {
    GetImageSettings();
  }, []);
  const handleRegenerateItemAction = () => {
    if (itemData?.appType) {
      handleRegenerateItem(itemData.appType);
    }
  };
  const creditsNeeded=useMemo(()=>{
    if(itemData?.appType===ImageSuiteApps.ImageRestore){
      return ImageSettingsData?.imageModels?.find((model:any)=>model?.modelVersion==="Flux")?.creditsNeeded;
    }else if(itemData?.appType===ImageSuiteApps.ImageToImage){
      return ImageSettingsData?.imageModels?.find((model:any)=>model?.modelVersion==="gpt-image-1")?.creditsNeeded;
    }else {
     return ImageSettingsData?.imageModels?.find((model:any)=>model?.modelName===itemData?.settings?.model)?.creditsNeeded
    }
    
  },[ImageSettingsData,itemData?.appType])
  const activeAppSuggestion = suggestionsImageApps.find(
    (app) => app.app === itemData?.appType
  );

  const handleCopyInputValue = (title: string) => {
    if (title) {
      navigator.clipboard.writeText(title);
      setIsTitleCoped(true);
      setTitleCoped(title);
      setTimeout(() => {
        setIsTitleCoped(false);
      }, 1000);
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          overflow: "hidden",
        }}
      >
        <Box className="app-info">
          <Box className="suite-app-name">
            <Box className="app-name-icn">
              {returnAppIcon(itemData?.appType || "")}
            </Box>
            <Box className="app-name-title">{itemData?.appType}</Box>
          </Box>
          {
            itemData?.appId && 
          <Box className="app-info-app-date">
            {convertUtcToLocalDateTime(itemData?.createdAt || "")}
          </Box>
          }
        </Box>
        <Box sx={{
          overflowY: "auto",
        }}>

        <Box className="app-details">
          <Box className="app-details-header">
            {itemData?.inputType &&
              itemData?.input?.imageUrl?.length > 0 &&
              hasFlag(itemData?.inputType, InputAndOutputTypes.Image) && (
                <Box className="app-details-header-title">Input image</Box>
              )}
          </Box>
          {itemData?.inputType &&
            itemData?.input?.imageUrl?.length > 0 &&
            hasFlag(itemData?.inputType, InputAndOutputTypes.Image) && (
              <Box className="app-details-input-image">
                <img src={itemData?.input.imageUrl} alt="" />
              </Box>
            )}
          <Box className="app-details-header">
            {itemData?.inputType &&
              hasFlag(itemData?.inputType, InputAndOutputTypes.Text) && (
                <Box className="app-details-header-title">Prompt</Box>
              )}
            {itemData?.inputType &&
              hasFlag(itemData?.inputType, InputAndOutputTypes.Text) && (
                <Box
                  onClick={() => handleCopyInputValue(itemData?.input.prompt)}
                >
                  <IconButton color="inherit" size="small">
                    <Tooltip
                      title={isTitleCoped ? "Copied" : "Copy prompt"}
                      arrow
                      placement="top"
                      componentsProps={tooltipStyles}
                    >
                      {isTitleCoped && itemData.input.prompt === titleCoped ? (
                        <DoneIcon
                          sx={{
                            ...OptionBtnIconStyle,
                            color: "var(--Main_icons_color)",
                            fontSize: "1rem !important",
                          }}
                        />
                      ) : (
                        <ContentCopyIcon
                          sx={{
                            ...OptionBtnIconStyle,
                            color: "var(--Main_icons_color)",
                            fontSize: "1rem !important",
                          }}
                        />
                      )}
                    </Tooltip>
                  </IconButton>
                </Box>
              )}
          </Box>

          {itemData?.inputType &&
            hasFlag(itemData?.inputType, InputAndOutputTypes.Text) && (
              <Box className="app-details-input">{itemData?.input.prompt}</Box>
            )}

          <Box className="app-details-selectors">
            {itemData?.settings.model && (
              <Box className="app-details-selector">
                <Typography className="label-name">Model</Typography>
                <Typography className="label-value">
                  {itemData?.settings.model}
                </Typography>
              </Box>
            )}
            {itemData?.settings.dimension && (
              <Box className="app-details-selector">
                <Typography className="label-name">Dimension</Typography>
                <Typography className="label-value">
                  {itemData?.settings.dimension}
                </Typography>
              </Box>
            )}
            {itemData?.settings.seed ? (
              <Box className="app-details-selector">
                <Typography className="label-name">Seed</Typography>
                <Typography className="label-value">
                  {itemData?.settings.seed}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            mt:"16px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "4rem",
          }}
          >
          {
            activeAppSuggestion?.suggestions && activeAppSuggestion?.suggestions.length > 0 && 
              <ReviewSuggestionsApps
                suggestionApps={activeAppSuggestion?.suggestions || []}
                activeApp={itemData}
              />
          }
        </Box>
        </Box>
      </Box>
      <Box pt={1}>
        <AppActionButton
          appMethodFire={handleRegenerateItemAction}
          isGenerateButtonDisabled={isLoading}
          IsGeneratePromptLoading={isLoading}
          inputValue={itemData?.input.prompt || itemData?.input.imageUrl || ""}
          creditsCount={itemData?.id ? (creditsNeeded ?
            creditsNeeded
            : appSuiteCosts?.Image ?? 0) : 0
          }
          btnTitle={btnActionText}
        />
      </Box>
    </Box>
  );
};
